import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  FileEdit, 
  Settings, 
  LogOut,
  CreditCard,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Zap,
  Users,
  Calendar,
  XCircle,
  Bell,
  Lock,
  ArrowDown,
  Clock,
  FileSignature,
  ExternalLink,
  ChevronDown,
  HelpCircle,
  Trash2,
  Link as LinkIcon,
  Plus,
  X,
  Building2,
  Mail,
  Wrench,
  FolderOpen,
  Send,
  FileCheck,
  FileText,
  Eye,
  Palette,
  Upload,
  ImageIcon
} from "lucide-react";
import Logo from "../components/Logo";
import AppSidebar from "@/components/AppSidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const PLANS = [
  { 
    id: "starter", 
    name: "Starter", 
    monthlyPrice: 19, 
    annualPrice: 192, 
    forms: "3 forms", 
    submissions: "100 submissions/month", 
    features: [
      "100 submissions/month", 
      "Unlimited automations",
      "AI lead scoring & summaries", 
      "Built-in CRM", 
      "Email notifications", 
      "Pipeline view",
      "Self-serve documentation"
    ] 
  },
  { 
    id: "professional", 
    name: "Professional", 
    monthlyPrice: 49, 
    annualPrice: 468, 
    forms: "10 forms", 
    submissions: "500 submissions/month", 
    features: [
      "500 submissions/month", 
      "Everything in Starter, plus:", 
      "CSV export", 
      "Custom form branding", 
      "Remove HeyIntake badge",
      "Team members",
      "Email support"
    ], 
    popular: true 
  }
];

// Helper to fire conversion events only once per session
const fireTrialConversion = (sessionId) => {
  const storageKey = `trial_conversion_fired_${sessionId}`;
  
  if (sessionStorage.getItem(storageKey)) {
    console.log('[Conversion] Already fired for this session, skipping');
    return Promise.resolve();
  }
  
  sessionStorage.setItem(storageKey, 'true');
  
  return new Promise((resolve) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'trial_started');
      window.gtag('event', 'conversion', {
        'send_to': 'AW-659695065/DRPvCNGZgIEcENnLyLoC',
        'event_callback': () => {
          console.log('[Conversion] Google Ads conversion fired successfully');
          resolve();
        }
      });
      setTimeout(resolve, 500);
    } else {
      console.warn('[Conversion] gtag not available');
      resolve();
    }
  });
};

// Standard link definitions
const STANDARD_LINKS = [
  { key: "feedback_link", label: "Feedback Link", placeholder: "https://forms.google.com/..." },
  { key: "onboarding_link", label: "Onboarding Link", placeholder: "https://notion.so/..." },
  { key: "agreement_link", label: "Agreement Link", placeholder: "https://docusign.com/..." },
  { key: "payment_link", label: "Payment Link", placeholder: "https://stripe.com/..." }
  // Note: portal_link removed - system auto-generates client portal links per submission
];

export default function SettingsPage() {
  const { user, token, logout, setUser } = useAuth();
  const { 
    subscriptionStatus, 
    refreshSubscription,
    isTrialing,
    isActive,
    isPastDue,
    isCanceling,
    planName,
    planPrice,
    leadsUsed,
    leadsLimit,
    stripeSubscriptionId
  } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Tab state - handle legacy "links" tab redirect to "tools"
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "links" ? "tools" : (tabParam || "general");
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // General Settings State
  const [generalData, setGeneralData] = useState({
    business_name: user?.business_name || "",
    business_email: user?.email || "",  // Business email for reply-to
    notification_email: user?.notification_email || user?.email || ""
  });
  const [savingGeneral, setSavingGeneral] = useState(false);
  
  // Password Change State
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  
  // Branding State (full object)
  const [businessBranding, setBusinessBranding] = useState({
    logo_url: null,
    primary_color: "#1a4d2e",
    button_color: "#1a4d2e",
    background_color: "#ffffff",
    text_color: "#1a1a1a"
  });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  // Outreach branding (Settings → Branding extension — flows into Lead Intel emails)
  const [outreachBranding, setOutreachBranding] = useState({
    sender_website: "",
    privacy_policy_url: "",
  });

  // Lead Intel outreach senders (workspace-level manual picker for OutreachPanel).
  const [outreachSenders, setOutreachSenders] = useState([]);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [senderDraft, setSenderDraft] = useState({ from_email: "", from_name: "" });
  const [savingSender, setSavingSender] = useState(false);
  
  // Links Tab State (now Tools Tab)
  const [schedulingLinks, setSchedulingLinks] = useState({
    calendly: "",
    google_calendar: "",
    square_appointments: ""
  });
  const [documentHandling, setDocumentHandling] = useState({
    method: "none",
    email: "",
    instructions: ""
  });
  const [standardLinks, setStandardLinks] = useState({
    feedback_link: "",
    onboarding_link: "",
    agreement_link: "",
    payment_link: "",
    portal_link: ""
  });
  const [customLinks, setCustomLinks] = useState([]);
  const [savingLinks, setSavingLinks] = useState(false);
  
  // Signing Settings State - REDESIGNED
  const [signingSettings, setSigningSettings] = useState({
    mode: "link",
    signing_link: "",
    provider: null,
    signwell_connected: false,
    docusign_connected: false
  });
  const [signwellApiKey, setSignwellApiKey] = useState("");
  const [savingSigningSettings, setSavingSigningSettings] = useState(false);
  const [signwellReplaceMode, setSignwellReplaceMode] = useState(false); // Replace key flow
  
  // Agreement Settings State
  const [agreementSettings, setAgreementSettings] = useState({
    enabled: false,
    mode: "link",  // "link" or "text"
    agreement_url: "",
    agreement_text: "",
    instructions: "",
    version: null
  });
  const [showAgreementPreview, setShowAgreementPreview] = useState(false);
  
  // Zapier Webhook State
  const [webhookSettings, setWebhookSettings] = useState({
    webhook_url: "",
    events: {
      new_submission: false,
      status_changed: false,
      intake_completed: false
    }
  });
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  
  // Notifications Tab State
  const [notificationSettings, setNotificationSettings] = useState({
    email_new_submission: true,
    email_reminders: false,
    email_digest: "none"
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  // Billing Tab State
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showCancelFeedback, setShowCancelFeedback] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelComment, setCancelComment] = useState("");
  const [billingCadence, setBillingCadence] = useState("annual");
  const [billingStatus, setBillingStatus] = useState({ enabled: true, disabled_reason: null, message: null });
  
  // Plan change modal
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  
  // Delete Account State
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Global Settings State Management
  const [savingSettings, setSavingSettings] = useState(false);
  const [initialSettings, setInitialSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Normalize values for consistent comparison (null/undefined → "")
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object" && !Array.isArray(value)) {
      const normalized = {};
      for (const key of Object.keys(value).sort()) {
        normalized[key] = normalizeValue(value[key]);
      }
      return normalized;
    }
    if (Array.isArray(value)) {
      return value.map(normalizeValue);
    }
    return value;
  };
  
  // Deep clone helper for state capture
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
  
  // Get current settings snapshot
  const getCurrentSettings = useCallback(() => {
    return {
      general: deepClone(generalData),
      brandColor: JSON.stringify(businessBranding),
      outreachBranding: deepClone(outreachBranding),
      scheduling: deepClone(schedulingLinks),
      documentHandling: deepClone(documentHandling),
      standardLinks: deepClone(standardLinks),
      signing: { mode: signingSettings.mode || "", signing_link: signingSettings.signing_link || "" },
      agreement: deepClone(agreementSettings),
      webhook: deepClone(webhookSettings),
      notifications: deepClone(notificationSettings)
    };
  }, [generalData, businessBranding, outreachBranding, schedulingLinks, documentHandling, standardLinks, signingSettings, agreementSettings, webhookSettings, notificationSettings]);
  
  // Calculate if any settings have changed (dirty state)
  const isDirty = useCallback(() => {
    if (!initialSettings || !settingsLoaded) return false;
    
    const current = normalizeValue(getCurrentSettings());
    const initial = normalizeValue(initialSettings);
    
    const currentStr = JSON.stringify(current);
    const initialStr = JSON.stringify(initial);
    
    return currentStr !== initialStr;
  }, [getCurrentSettings, initialSettings, settingsLoaded]);
  
  // Store initial settings after fetch (for dirty state detection)
  const captureInitialSettings = useCallback(() => {
    const snapshot = getCurrentSettings();
    setInitialSettings(snapshot);
    setSettingsLoaded(true);
  }, [getCurrentSettings]);
  
  // Update form data when user context changes
  useEffect(() => {
    if (user) {
      setGeneralData({
        business_name: user.business_name || "",
        business_email: user.email || "",
        notification_email: user.notification_email || user.email || ""
      });
      setIsGoogleUser(!!user?.google_id);
    }
  }, [user?.business_name, user?.notification_email, user?.email, user?.google_id]);
  
  // Fetch all settings on mount
  useEffect(() => {
    fetchUsage();
    refreshSubscription();
    fetchNotificationSettings();
    fetchAllLinks();
    fetchBillingStatus();
    fetchBusinessBranding();
    fetchOutreachBranding();
    fetchOutreachSenders();
  }, []);

  const fetchOutreachSenders = async () => {
    setSendersLoading(true);
    try {
      const response = await axios.get(`${API}/lead-intel/outreach/senders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutreachSenders(response.data?.senders || []);
    } catch (e) {
      // 403 if Lead Intelligence not entitled — silently skip
    } finally {
      setSendersLoading(false);
    }
  };

  const addOutreachSender = async () => {
    const email = (senderDraft.from_email || "").trim().toLowerCase();
    const name = (senderDraft.from_name || "").trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid sender email");
      return;
    }
    setSavingSender(true);
    try {
      await axios.post(`${API}/lead-intel/outreach/senders`,
        { from_email: email, from_name: name, active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSenderDraft({ from_email: "", from_name: "" });
      toast.success("Sender added");
      fetchOutreachSenders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add sender");
    } finally {
      setSavingSender(false);
    }
  };

  const updateOutreachSender = async (sender, patch) => {
    try {
      await axios.patch(`${API}/lead-intel/outreach/senders/${sender.id}`,
        {
          from_email: patch.from_email ?? sender.from_email,
          from_name: patch.from_name ?? sender.from_name ?? "",
          active: patch.active ?? sender.active,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOutreachSenders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update sender");
    }
  };

  const deleteOutreachSender = async (sender) => {
    if (!window.confirm(`Remove ${sender.from_email} from your outreach sender pool?`)) return;
    try {
      await axios.delete(`${API}/lead-intel/outreach/senders/${sender.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sender removed");
      fetchOutreachSenders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove sender");
    }
  };

  const fetchOutreachBranding = async () => {
    try {
      const response = await axios.get(`${API}/lead-intel/outreach/defaults`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutreachBranding({
        sender_website: response.data?.sender_website || "",
        privacy_policy_url: response.data?.privacy_policy_url || "",
      });
    } catch (e) {
      // Workspaces without lead_intelligence entitlement return 403 — silently ignore
    }
  };
  
  const fetchBusinessBranding = async () => {
    try {
      const response = await axios.get(`${API}/business/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const b = response.data.branding || {};
      setBusinessBranding(prev => ({
        ...prev,
        logo_url: b.logo_url || null,
        primary_color: b.primary_color || b.brand_color || "#1a4d2e",
        button_color: b.button_color || b.primary_color || b.brand_color || "#1a4d2e",
        background_color: b.background_color || "#ffffff",
        text_color: b.text_color || "#1a1a1a"
      }));
    } catch (e) {
      console.error("Failed to fetch branding", e);
    }
  };

  const handleBusinessLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max size: 2MB"); return; }
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) { toast.error("Allowed: PNG, JPEG, WebP, SVG"); return; }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const resp = await axios.post(`${API}/business/logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setBusinessBranding(prev => ({ ...prev, logo_url: resp.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleBusinessLogoDelete = async () => {
    try {
      await axios.delete(`${API}/business/logo`, { headers: { Authorization: `Bearer ${token}` } });
      setBusinessBranding(prev => ({ ...prev, logo_url: null }));
      toast.success("Logo removed");
    } catch { toast.error("Failed to remove logo"); }
  };
  
  // Capture initial settings ONCE after page mount + a delay to let
  // async fetches finish. Uses a ref to the *latest* capture function so
  // the timer always runs against the most recent state (avoids the
  // race where the timer fires with stale state and the user's
  // post-edit value equals the captured "initial" value, leaving Save
  // disabled even after a real edit).
  const initialCapturedRef = useRef(false);
  const captureRef = useRef(captureInitialSettings);
  useEffect(() => { captureRef.current = captureInitialSettings; }, [captureInitialSettings]);
  useEffect(() => {
    if (initialCapturedRef.current) return;
    if (!user) return;
    initialCapturedRef.current = true;
    // 1.5s gives in-flight fetches time to populate. The snapshot uses
    // the LATEST capture function via captureRef so any state that
    // arrived before the timer fires is correctly reflected.
    const timer = setTimeout(() => {
      captureRef.current?.();
    }, 1500);
    return () => clearTimeout(timer);
  }, [user]);
  
  // Check for Stripe success/cancel
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (paymentStatus === "success" && sessionId) {
      setCheckingPayment(true);
      verifyPaymentAndRefresh(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Checkout was cancelled");
    }
  }, [searchParams]);
  
  // Handle section scroll from URL params (e.g., ?tab=tools&section=agreement)
  useEffect(() => {
    const sectionParam = searchParams.get("section");
    if (sectionParam && settingsLoaded) {
      // Give time for tab to render, then scroll to section
      const scrollTimer = setTimeout(() => {
        const sectionMap = {
          'agreement': 'agreement-section',
          'signing': 'signing-section',
          'scheduling': 'scheduling-section',
          'documents': 'documents-section',
          'branding': 'branding-section'
        };
        
        const elementId = sectionMap[sectionParam];
        if (elementId) {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Add highlight effect
            element.classList.add('ring-2', 'ring-primary/30', 'ring-offset-2');
            setTimeout(() => {
              element.classList.remove('ring-2', 'ring-primary/30', 'ring-offset-2');
            }, 2000);
          }
        }
      }, 300);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [searchParams, settingsLoaded]);
  
  const verifyPaymentAndRefresh = async (sessionId) => {
    try {
      await axios.post(`${API}/billing/verify-session`, { session_id: sessionId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fireTrialConversion(sessionId);
      await refreshSubscription();
      
      toast.success("Welcome! Your trial has started.");
      navigate("/settings", { replace: true });
    } catch (error) {
      console.error("Payment verification error:", error);
      toast.error("There was an issue verifying your payment. Please contact support.");
    } finally {
      setCheckingPayment(false);
    }
  };

  // ==================== FETCH FUNCTIONS ====================
  
  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/business/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch usage", error);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/business/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setNotificationSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch notification settings", error);
    }
  };

  const fetchAllLinks = async () => {
    try {
      // Fetch from IntegrationSettings (single source of truth)
      const response = await axios.get(`${API}/workflow/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settings = response.data || {};
      
      // Set scheduling links
      setSchedulingLinks({
        calendly: settings.scheduling_link || "",
        google_calendar: settings.calendar_link || "",
        square_appointments: settings.square_link || ""
      });
      
      // Set standard links
      setStandardLinks({
        feedback_link: settings.feedback_link || "",
        onboarding_link: settings.onboarding_link || "",
        agreement_link: settings.agreement_link || "",
        payment_link: settings.payment_link || "",
        portal_link: settings.portal_link || ""
      });
      
      // Set custom links
      const customLinksObj = settings.custom_links || {};
      setCustomLinks(
        Object.entries(customLinksObj).map(([label, url]) => ({ label, url }))
      );
      
      // Fetch document handling separately (legacy endpoint)
      try {
        const docResponse = await axios.get(`${API}/business/document-handling`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (docResponse.data) {
          setDocumentHandling({
            method: docResponse.data.method || "none",
            email: docResponse.data.email || "",
            instructions: docResponse.data.instructions || ""
          });
        }
      } catch (e) {
        console.error("Failed to fetch document handling", e);
      }
      
      // Fetch signing settings
      try {
        const signingResponse = await axios.get(`${API}/business/signing-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (signingResponse.data) {
          setSigningSettings({
            mode: signingResponse.data.mode || "link",
            signing_link: signingResponse.data.signing_link || "",
            provider: signingResponse.data.provider || null,
            signwell_connected: signingResponse.data.signwell_connected || false,
            docusign_connected: signingResponse.data.docusign_connected || false
          });
        }
      } catch (e) {
        console.error("Failed to fetch signing settings", e);
      }
      
      // Fetch webhook settings
      try {
        const webhookResponse = await axios.get(`${API}/business/webhook-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (webhookResponse.data) {
          setWebhookSettings({
            webhook_url: webhookResponse.data.webhook_url || "",
            events: webhookResponse.data.events || {
              new_submission: false,
              status_changed: false,
              intake_completed: false
            }
          });
        }
      } catch (e) {
        console.error("Failed to fetch webhook settings", e);
      }
      
      // Fetch agreement settings
      try {
        const agreementResponse = await axios.get(`${API}/business/agreement-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (agreementResponse.data) {
          setAgreementSettings({
            enabled: agreementResponse.data.enabled || false,
            mode: agreementResponse.data.mode || "link",
            agreement_url: agreementResponse.data.agreement_url || "",
            agreement_text: agreementResponse.data.agreement_text || "",
            instructions: agreementResponse.data.instructions || "",
            version: agreementResponse.data.version || null
          });
        }
      } catch (e) {
        console.error("Failed to fetch agreement settings", e);
      }
      
    } catch (error) {
      console.error("Failed to fetch links", error);
    }
  };
  
  const fetchBillingStatus = async () => {
    try {
      const response = await axios.get(`${API}/billing/status`);
      setBillingStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch billing status", error);
      setBillingStatus({ enabled: true, disabled_reason: null, message: null });
    }
  };

  // ==================== GLOBAL SAVE FUNCTION ====================
  
  const saveAllSettings = async () => {
    // Validation
    if (!generalData.business_email) {
      toast.error("Business email is required");
      return;
    }
    
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(generalData.business_email)) {
      toast.error("Please enter a valid business email");
      return;
    }
    
    // Agreement validation
    if (agreementSettings.enabled) {
      if (agreementSettings.mode === "link" && !agreementSettings.agreement_url) {
        toast.error("Please enter an agreement link");
        return;
      }
      if (agreementSettings.mode === "link" && !agreementSettings.agreement_url.startsWith("https://")) {
        toast.error("Agreement link must start with https://");
        return;
      }
      if (agreementSettings.mode === "text" && !agreementSettings.agreement_text) {
        toast.error("Please enter your agreement text");
        return;
      }
    }
    
    setSavingSettings(true);
    try {
      // 1. Save General/Profile Settings
      await axios.put(`${API}/business/profile`, {
        business_name: generalData.business_name,
        business_email: generalData.business_email,
        notification_email: generalData.notification_email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user context
      setUser(prev => ({ 
        ...prev, 
        business_name: generalData.business_name,
        email: generalData.business_email,
        notification_email: generalData.notification_email 
      }));
      
      // 1b. Save Branding (full object)
      await axios.put(`${API}/business/branding`, {
        primary_color: businessBranding.primary_color,
        button_color: businessBranding.button_color,
        background_color: businessBranding.background_color,
        text_color: businessBranding.text_color
      }, { headers: { Authorization: `Bearer ${token}` } });

      // 1c. Save Outreach branding (Lead Intelligence email defaults).
      // Silently skip on 403 (workspace without lead_intelligence access).
      try {
        await axios.put(`${API}/lead-intel/outreach/defaults`, {
          sender_website: outreachBranding.sender_website || "",
          privacy_policy_url: outreachBranding.privacy_policy_url || "",
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {
        if (e.response?.status !== 403) throw e;
      }
      
      // 2. Save Links/Tools Settings
      const customLinksObj = {};
      customLinks.forEach(link => {
        if (link.label && link.url) {
          const sanitizedLabel = link.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          customLinksObj[sanitizedLabel] = link.url;
        }
      });
      
      await axios.put(`${API}/workflow/settings`, {
        scheduling_link: schedulingLinks.calendly || null,
        calendar_link: schedulingLinks.google_calendar || null,
        square_link: schedulingLinks.square_appointments || null,
        feedback_link: standardLinks.feedback_link || null,
        onboarding_link: standardLinks.onboarding_link || null,
        agreement_link: standardLinks.agreement_link || null,
        payment_link: standardLinks.payment_link || null,
        portal_link: standardLinks.portal_link || null,
        custom_links: customLinksObj
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 3. Save Document Handling
      await axios.put(`${API}/business/document-handling`, documentHandling, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 4. Save Signing Settings
      const signingPayload = {
        mode: signingSettings.mode,
        signing_link: signingSettings.signing_link
      };
      if (signwellApiKey) {
        signingPayload.signwell_api_key = signwellApiKey;
      }
      await axios.put(`${API}/business/signing-settings`, signingPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignwellApiKey("");
      
      // 5. Save Agreement Settings
      await axios.put(`${API}/business/agreement-settings`, {
        enabled: agreementSettings.enabled,
        mode: agreementSettings.mode,
        agreement_url: agreementSettings.agreement_url || null,
        agreement_text: agreementSettings.agreement_text || null,
        instructions: agreementSettings.instructions || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 6. Save Webhook Settings
      await axios.put(`${API}/business/webhook-settings`, webhookSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 7. Save Notification Settings
      await axios.put(`${API}/business/notification-settings`, notificationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset dirty state IMMEDIATELY by capturing current state as new initial
      // Use getCurrentSettings() to get the exact same structure we compare against
      const savedState = getCurrentSettings();
      setInitialSettings(savedState);
      
      toast.success("Settings saved successfully");
    } catch (error) {
      // Revert all state to last saved values
      if (initialSettings) {
        setGeneralData(JSON.parse(JSON.stringify(initialSettings.general || {})));
        if (initialSettings.brandColor !== undefined) {
          try { setBusinessBranding(JSON.parse(initialSettings.brandColor)); } catch {}
        }
        setSchedulingLinks(JSON.parse(JSON.stringify(initialSettings.scheduling || {})));
        setDocumentHandling(JSON.parse(JSON.stringify(initialSettings.documentHandling || {})));
        setStandardLinks(JSON.parse(JSON.stringify(initialSettings.standardLinks || {})));
        setAgreementSettings(JSON.parse(JSON.stringify(initialSettings.agreement || {})));
        setWebhookSettings(JSON.parse(JSON.stringify(initialSettings.webhook || {})));
        setNotificationSettings(JSON.parse(JSON.stringify(initialSettings.notifications || {})));
        if (initialSettings.outreachBranding) {
          setOutreachBranding(JSON.parse(JSON.stringify(initialSettings.outreachBranding)));
        }
      }
      toast.error("Couldn't save changes");
    } finally {
      setSavingSettings(false);
    }
  };

  // ==================== INDIVIDUAL SAVE FUNCTIONS (Legacy) ====================
  
  const saveGeneralSettings = async () => {
    if (!generalData.business_email) {
      toast.error("Business email is required");
      return;
    }
    
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(generalData.business_email)) {
      toast.error("Please enter a valid business email");
      return;
    }
    
    setSavingGeneral(true);
    try {
      await axios.put(`${API}/business/profile`, {
        business_name: generalData.business_name,
        business_email: generalData.business_email,
        notification_email: generalData.notification_email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user context
      setUser(prev => ({ 
        ...prev, 
        business_name: generalData.business_name,
        email: generalData.business_email,
        notification_email: generalData.notification_email 
      }));
      
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingGeneral(false);
    }
  };

  const saveAllLinks = async () => {
    setSavingLinks(true);
    try {
      // Build the integration settings payload
      const customLinksObj = {};
      customLinks.forEach(link => {
        if (link.label && link.url) {
          // Sanitize label to be a valid variable name
          const sanitizedLabel = link.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          customLinksObj[sanitizedLabel] = link.url;
        }
      });
      
      const payload = {
        // Scheduling links
        scheduling_link: schedulingLinks.calendly || null,
        calendar_link: schedulingLinks.google_calendar || null,
        square_link: schedulingLinks.square_appointments || null,
        // Standard links
        feedback_link: standardLinks.feedback_link || null,
        onboarding_link: standardLinks.onboarding_link || null,
        agreement_link: standardLinks.agreement_link || null,
        payment_link: standardLinks.payment_link || null,
        portal_link: standardLinks.portal_link || null,
        // Custom links
        custom_links: customLinksObj
      };
      
      await axios.put(`${API}/workflow/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save document handling settings
      await axios.put(`${API}/business/document-handling`, documentHandling, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save signing settings
      const signingPayload = {
        mode: signingSettings.mode,
        signing_link: signingSettings.signing_link
      };
      if (signwellApiKey) {
        signingPayload.signwell_api_key = signwellApiKey;
      }
      await axios.put(`${API}/business/signing-settings`, signingPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear the API key input after save
      setSignwellApiKey("");
      
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingLinks(false);
    }
  };
  
  // Disconnect signing provider
  const disconnectSigningProvider = async (provider) => {
    setSavingSigningSettings(true);
    try {
      await axios.put(`${API}/business/signing-settings`, {
        disconnect_provider: provider
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (provider === "signwell") {
        setSigningSettings(prev => ({ ...prev, signwell_connected: false, provider: null, mode: "link" }));
        setSignwellApiKey(""); // Clear the API key input
        setSignwellReplaceMode(false); // Reset replace mode
      } else if (provider === "docusign") {
        setSigningSettings(prev => ({ ...prev, docusign_connected: false }));
      }
      
      toast.success(`${provider === "signwell" ? "SignWell" : "DocuSign"} disconnected`);
    } catch (error) {
      toast.error("Failed to disconnect provider");
    } finally {
      setSavingSigningSettings(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/business/notification-settings`, notificationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error("Failed to save notification settings");
    } finally {
      setSavingNotifications(false);
    }
  };
  
  const saveAgreementSettings = async () => {
    // Validation
    if (agreementSettings.enabled) {
      if (agreementSettings.mode === "link" && !agreementSettings.agreement_url) {
        toast.error("Please enter an agreement link");
        return;
      }
      if (agreementSettings.mode === "link" && !agreementSettings.agreement_url.startsWith("https://")) {
        toast.error("Agreement link must start with https://");
        return;
      }
      if (agreementSettings.mode === "text" && !agreementSettings.agreement_text) {
        toast.error("Please enter your agreement text");
        return;
      }
    }
    
    setSavingAgreement(true);
    try {
      const response = await axios.put(`${API}/business/agreement-settings`, {
        enabled: agreementSettings.enabled,
        mode: agreementSettings.mode,
        agreement_url: agreementSettings.agreement_url || null,
        agreement_text: agreementSettings.agreement_text || null,
        instructions: agreementSettings.instructions || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update version from response
      if (response.data?.version) {
        setAgreementSettings(prev => ({ ...prev, version: response.data.version }));
      }
      
      toast.success("Agreement settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save agreement settings");
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // ==================== BILLING FUNCTIONS ====================
  
  const handleStartTrial = async (planId) => {
    if (!billingStatus.enabled) {
      toast.error("Billing is currently unavailable");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/billing/create-checkout-session`, {
        plan_id: planId,
        cadence: billingCadence
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePlan = async (newPlanId) => {
    setChangingPlan(true);
    try {
      await axios.post(`${API}/subscription/change-plan`, { 
        new_plan_id: newPlanId,
        billing_cadence: billingCadence
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSubscription();
      setShowChangePlan(false);
      toast.success(`Upgraded to ${newPlanId === 'professional' ? 'Professional' : 'Starter'}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change plan");
    } finally {
      setChangingPlan(false);
    }
  };

  const handleScheduleDowngrade = async (targetPlan) => {
    setChangingPlan(true);
    try {
      await axios.post(`${API}/subscription/schedule-downgrade`, { 
        target_plan: targetPlan 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSubscription();
      setShowChangePlan(false);
      toast.success("Downgrade scheduled for next billing cycle");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule downgrade");
    } finally {
      setChangingPlan(false);
    }
  };

  const handleUpgradeNow = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/billing/upgrade-from-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSubscription();
      toast.success("You're now on the full plan!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upgrade");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      await axios.post(`${API}/billing/cancel-subscription`, {
        reason: cancelReason,
        comment: cancelComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSubscription();
      toast.success("Subscription cancellation scheduled");
      setShowCancelFeedback(false);
      setCancelReason("");
      setCancelComment("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/billing/reactivate-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSubscription();
      toast.success("Subscription reactivated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDowngrade = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/billing/cancel-downgrade`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshSubscription();
      toast.success("Downgrade cancelled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel downgrade");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "delete") {
      toast.error('Please type "delete" to confirm');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        data: { reason: deleteReason },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Account deleted successfully");
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeletingAccount(false);
    }
  };

  const openCancelFeedback = () => {
    setShowCancelFeedback(true);
  };

  // ==================== HELPER FUNCTIONS ====================
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  const addCustomLink = () => {
    setCustomLinks([...customLinks, { label: "", url: "" }]);
  };
  
  const removeCustomLink = (index) => {
    setCustomLinks(customLinks.filter((_, i) => i !== index));
  };
  
  const updateCustomLink = (index, field, value) => {
    const updated = [...customLinks];
    updated[index][field] = value;
    setCustomLinks(updated);
  };

  // ==================== WEBHOOK FUNCTIONS ====================
  
  const saveWebhookSettings = async () => {
    // Validate URL if provided
    if (webhookSettings.webhook_url && !webhookSettings.webhook_url.startsWith("https://")) {
      toast.error("Webhook URL must start with https://");
      return;
    }
    
    try {
      setSavingWebhook(true);
      await axios.put(`${API}/business/webhook-settings`, webhookSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Webhook settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save webhook settings");
    } finally {
      setSavingWebhook(false);
    }
  };
  
  const testWebhook = async () => {
    if (!webhookSettings.webhook_url) {
      toast.error("Please enter a webhook URL first");
      return;
    }
    
    if (!webhookSettings.webhook_url.startsWith("https://")) {
      toast.error("Webhook URL must start with https://");
      return;
    }
    
    try {
      setTestingWebhook(true);
      // Save first to ensure URL is stored
      await axios.put(`${API}/business/webhook-settings`, webhookSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Then test
      await axios.post(`${API}/business/webhook-test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test webhook sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test webhook");
    } finally {
      setTestingWebhook(false);
    }
  };

  // ==================== RENDER ====================
  
  return (
    <div className="flex min-h-screen bg-zinc-50" data-testid="settings-page">
      <AppSidebar />
      
      <main className="flex-1 ml-64">
        {/* Sticky Header with Save Button */}
        <div className="sticky top-0 z-40 bg-zinc-50/95 backdrop-blur-sm border-b border-border/50 px-6 lg:px-8 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Settings</h1>
              <p className="text-muted-foreground mt-1">Manage your account, links, and preferences</p>
            </div>
            
            {/* Global Save Button with Dirty State */}
            <div className="flex items-center gap-3">
              {isDirty() && (
                <span className="text-sm text-amber-600 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                  Unsaved changes
                </span>
              )}
              <Button 
                onClick={saveAllSettings} 
                disabled={savingSettings || !isDirty()}
                className={`rounded-full transition-all ${!isDirty() ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="global-save-btn"
              >
                {savingSettings ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="p-6 lg:p-8">
          <div className="max-w-4xl mx-auto">
          
          {/* Payment Verification Banner */}
          {checkingPayment && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3"
            >
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-blue-800">Verifying your payment...</span>
            </motion.div>
          )}
          
          {/* Tabbed Settings */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 h-12 p-1 bg-muted/50 rounded-xl" data-testid="settings-tabs">
              <TabsTrigger 
                value="general" 
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-general"
              >
                <Building2 className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger 
                value="tools" 
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-tools"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Tools
              </TabsTrigger>
              <TabsTrigger 
                value="notifications" 
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-notifications"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger 
                value="billing" 
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-billing"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Billing
              </TabsTrigger>
            </TabsList>
            
            {/* ==================== GENERAL TAB ==================== */}
            <TabsContent value="general" className="space-y-6">
              {/* Business Profile */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle>Business Profile</CardTitle>
                  <CardDescription>Your business information and contact details.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-6 max-w-lg">
                    <div className="space-y-2">
                      <Label htmlFor="business_name">Business Name</Label>
                      <Input
                        id="business_name"
                        value={generalData.business_name}
                        onChange={(e) => setGeneralData({ ...generalData, business_name: e.target.value })}
                        data-testid="business-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="business_email" className="flex items-center gap-2">
                        Business Email <Badge variant="secondary" className="text-xs">Required</Badge>
                      </Label>
                      <Input
                        id="business_email"
                        type="email"
                        value={generalData.business_email}
                        onChange={(e) => setGeneralData({ ...generalData, business_email: e.target.value })}
                        placeholder="hello@yourbusiness.com"
                        data-testid="business-email-input"
                      />
                      <p className="text-sm text-muted-foreground">
                        This email will be used as the "reply-to" address for all outgoing emails from HeyIntake.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notification_email">Notification Email</Label>
                      <Input
                        id="notification_email"
                        type="email"
                        value={generalData.notification_email}
                        onChange={(e) => setGeneralData({ ...generalData, notification_email: e.target.value })}
                        data-testid="notification-email-input"
                      />
                      <p className="text-sm text-muted-foreground">
                        Lead notifications will be sent to this email.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sender_website">Sender website</Label>
                      <Input
                        id="sender_website"
                        type="url"
                        placeholder="https://yourstudio.com"
                        value={outreachBranding.sender_website}
                        onChange={(e) => setOutreachBranding(prev => ({ ...prev, sender_website: e.target.value }))}
                        data-testid="settings-sender-website-input"
                      />
                      <p className="text-sm text-muted-foreground">
                        Displayed in the footer of Lead Intelligence outreach emails.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="privacy_policy_url">Privacy policy URL</Label>
                      <Input
                        id="privacy_policy_url"
                        type="url"
                        placeholder="https://yourstudio.com/privacy"
                        value={outreachBranding.privacy_policy_url}
                        onChange={(e) => setOutreachBranding(prev => ({ ...prev, privacy_policy_url: e.target.value }))}
                        data-testid="settings-privacy-policy-input"
                      />
                      <p className="text-sm text-muted-foreground">
                        Appears as a footer link in outbound outreach emails — required by some inbox providers.
                      </p>
                    </div>

                    {/* Lead Intelligence — Outreach Senders (manual picker pool) */}
                    <div id="outreach-senders" className="space-y-3 pt-4 border-t border-zinc-100" data-testid="outreach-senders-section">
                      <div>
                        <Label className="text-base">Outreach senders</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Configure alternate From addresses for Lead Intelligence outreach
                          (e.g. <code className="text-xs bg-zinc-100 px-1 rounded">alex@tryheyintake.com</code>).
                          You'll pick which one to send from per email in the lead drawer.
                          <strong> Each domain must be verified in your Resend account (SPF + DKIM)</strong> or
                          Resend will reject the send. Replies always route to your business email.
                        </p>
                      </div>

                      {/* Existing senders list */}
                      <div className="space-y-2" data-testid="outreach-senders-list">
                        {sendersLoading ? (
                          <div className="text-sm text-zinc-400 italic">Loading…</div>
                        ) : outreachSenders.length === 0 ? (
                          <div className="text-sm text-zinc-500 italic border border-dashed border-zinc-200 rounded-md px-3 py-3">
                            No alternate senders yet. Outreach uses your workspace default.
                          </div>
                        ) : (
                          outreachSenders.map(s => (
                            <div
                              key={s.id}
                              className="flex flex-wrap items-center gap-2 border border-zinc-200 rounded-md px-3 py-2"
                              data-testid={`outreach-sender-row-${s.id}`}
                            >
                              <Input
                                type="email"
                                defaultValue={s.from_email}
                                onBlur={(e) => {
                                  const v = (e.target.value || "").trim().toLowerCase();
                                  if (v && v !== s.from_email) updateOutreachSender(s, { from_email: v });
                                }}
                                className="flex-1 min-w-[200px]"
                                data-testid={`outreach-sender-email-${s.id}`}
                              />
                              <Input
                                type="text"
                                placeholder="Display name (optional)"
                                defaultValue={s.from_name || ""}
                                onBlur={(e) => {
                                  const v = (e.target.value || "").trim();
                                  if (v !== (s.from_name || "")) updateOutreachSender(s, { from_name: v });
                                }}
                                className="flex-1 min-w-[160px]"
                                data-testid={`outreach-sender-name-${s.id}`}
                              />
                              <label className="flex items-center gap-1.5 text-xs text-zinc-600 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={!!s.active}
                                  onChange={(e) => updateOutreachSender(s, { active: e.target.checked })}
                                  data-testid={`outreach-sender-active-${s.id}`}
                                />
                                Active
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteOutreachSender(s)}
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                data-testid={`outreach-sender-delete-${s.id}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add new sender */}
                      <div className="flex flex-wrap items-end gap-2 pt-1" data-testid="outreach-sender-add-row">
                        <div className="flex-1 min-w-[200px]">
                          <Label htmlFor="new-sender-email" className="text-xs">New sender email</Label>
                          <Input
                            id="new-sender-email"
                            type="email"
                            placeholder="alex@tryheyintake.com"
                            value={senderDraft.from_email}
                            onChange={(e) => setSenderDraft(d => ({ ...d, from_email: e.target.value }))}
                            data-testid="outreach-sender-new-email"
                          />
                        </div>
                        <div className="flex-1 min-w-[160px]">
                          <Label htmlFor="new-sender-name" className="text-xs">Display name (optional)</Label>
                          <Input
                            id="new-sender-name"
                            type="text"
                            placeholder="Alex from HeyIntake"
                            value={senderDraft.from_name}
                            onChange={(e) => setSenderDraft(d => ({ ...d, from_name: e.target.value }))}
                            data-testid="outreach-sender-new-name"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={addOutreachSender}
                          disabled={savingSender || !senderDraft.from_email}
                          data-testid="outreach-sender-add-btn"
                        >
                          {savingSender ? "Adding…" : "Add sender"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Password Change */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Security
                  </CardTitle>
                  <CardDescription>
                    {isGoogleUser 
                      ? "You signed in with Google. Password management is handled through your Google account."
                      : "Update your password to keep your account secure."
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isGoogleUser ? (
                    <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl max-w-lg">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium">Signed in with Google</p>
                        <p className="text-sm text-muted-foreground">Manage your password through your Google account settings.</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4 max-w-lg">
                        <div className="space-y-2">
                          <Label htmlFor="current_password">Current Password</Label>
                          <Input
                            id="current_password"
                            type="password"
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                            placeholder="Enter current password"
                            data-testid="current-password-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new_password">New Password</Label>
                          <Input
                            id="new_password"
                            type="password"
                            value={passwordData.new_password}
                            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                            placeholder="Enter new password"
                            data-testid="new-password-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirm_password">Confirm New Password</Label>
                          <Input
                            id="confirm_password"
                            type="password"
                            value={passwordData.confirm_password}
                            onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                            placeholder="Confirm new password"
                            data-testid="confirm-password-input"
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={handleChangePassword} 
                        disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
                        className="rounded-full"
                        data-testid="change-password-btn"
                      >
                        {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Change Password
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
              
              {/* Branding */}
              <Card id="branding-section" className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Branding
                  </CardTitle>
                  <CardDescription>Default branding for forms, emails, and client portal. Forms can override these settings individually.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Logo */}
                  <div>
                    <Label className="mb-2 block font-medium">Logo</Label>
                    <div className="flex items-center gap-4">
                      {businessBranding.logo_url ? (
                        <div className="relative group">
                          <img
                            src={businessBranding.logo_url.startsWith("http") ? businessBranding.logo_url : `${process.env.REACT_APP_BACKEND_URL}${businessBranding.logo_url}`}
                            alt="Business logo"
                            className="h-16 w-auto max-w-[200px] object-contain rounded-lg border border-slate-200 p-2"
                            data-testid="settings-logo-preview"
                          />
                          <button
                            onClick={handleBusinessLogoDelete}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid="settings-delete-logo-btn"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => logoInputRef.current?.click()}
                          className="h-16 w-40 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center gap-2 cursor-pointer hover:border-slate-300 transition-colors"
                          data-testid="settings-logo-upload-zone"
                        >
                          {logoUploading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-400">Upload logo</span>
                            </>
                          )}
                        </div>
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={handleBusinessLogoUpload}
                      />
                      {businessBranding.logo_url && (
                        <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
                          {logoUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                          Replace
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">PNG, JPEG, WebP, or SVG. Max 2MB.</p>
                  </div>

                  {/* Colors Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                    {[
                      { key: "primary_color", label: "Primary Color", desc: "Header, links, accents" },
                      { key: "button_color", label: "Button Color", desc: "CTA buttons" },
                      { key: "background_color", label: "Background", desc: "Page background" },
                      { key: "text_color", label: "Text Color", desc: "Body text" }
                    ].map(({ key, label, desc }) => (
                      <div key={key}>
                        <Label className="text-sm mb-1 block">{label}</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={businessBranding[key]}
                            onChange={(e) => setBusinessBranding(prev => ({ ...prev, [key]: e.target.value }))}
                            className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer"
                            data-testid={`settings-${key}-picker`}
                          />
                          <Input
                            value={businessBranding[key]}
                            onChange={(e) => setBusinessBranding(prev => ({ ...prev, [key]: e.target.value }))}
                            className="w-24 font-mono text-xs h-9"
                            data-testid={`settings-${key}-input`}
                          />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* Preview */}
                  <div className="rounded-xl border border-slate-200 overflow-hidden max-w-lg">
                    <div className="px-4 py-3 text-center" style={{ backgroundColor: businessBranding.primary_color }}>
                      {businessBranding.logo_url ? (
                        <img
                          src={businessBranding.logo_url.startsWith("http") ? businessBranding.logo_url : `${process.env.REACT_APP_BACKEND_URL}${businessBranding.logo_url}`}
                          alt="Preview"
                          className="h-8 mx-auto object-contain"
                        />
                      ) : (
                        <span className="text-sm font-semibold text-white">{user?.business_name || "Your Business"}</span>
                      )}
                    </div>
                    <div className="p-4" style={{ backgroundColor: businessBranding.background_color }}>
                      <p className="text-sm mb-2" style={{ color: businessBranding.text_color }}>Preview of your branded experience</p>
                      <button className="px-4 py-1.5 rounded-md text-sm font-medium text-white" style={{ backgroundColor: businessBranding.button_color }}>
                        Submit
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Delete Account */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] border-red-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <Trash2 className="w-5 h-5" />
                    Delete Account
                  </CardTitle>
                  <CardDescription>
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="rounded-full" data-testid="delete-account-btn">
                        Delete Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete your account, all forms, submissions, and data. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Reason for leaving (optional)</Label>
                          <Select value={deleteReason} onValueChange={setDeleteReason}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a reason..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_needed">No longer need the service</SelectItem>
                              <SelectItem value="alternative">Using another solution</SelectItem>
                              <SelectItem value="too_expensive">Too expensive</SelectItem>
                              <SelectItem value="technical">Technical issues</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Type "delete" to confirm</Label>
                          <Input
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            placeholder="delete"
                            data-testid="delete-confirmation-input"
                          />
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleDeleteAccount}
                          disabled={deletingAccount || deleteConfirmation !== "delete"}
                          className="bg-destructive hover:bg-destructive/90"
                          data-testid="confirm-delete-btn"
                        >
                          {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete Account"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* ==================== TOOLS TAB ==================== */}
            <TabsContent value="tools" className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Tools are your single source of truth.</strong> All links defined here can be used as variables in email templates using the <code className="bg-blue-100 px-1 rounded">{"{{variable_name}}"}</code> syntax.
                </p>
              </div>
              
              {/* SECTION 1: Scheduling */}
              <Card id="scheduling-section" className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    Scheduling
                    {(schedulingLinks.calendly || schedulingLinks.google_calendar || schedulingLinks.square_appointments) ? (
                      <Badge className="bg-green-100 text-green-700 text-xs font-normal ml-auto">Configured</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-xs font-normal ml-auto">Not set</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Connect your scheduling tools to easily include booking links in emails.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 max-w-lg">
                    <div className="space-y-2">
                      <Label htmlFor="calendly">Calendly</Label>
                      <Input
                        id="calendly"
                        placeholder="https://calendly.com/your-username"
                        value={schedulingLinks.calendly}
                        onChange={(e) => setSchedulingLinks({ ...schedulingLinks, calendly: e.target.value })}
                        data-testid="calendly-input"
                      />
                      <p className="text-xs text-muted-foreground">Available as <code className="bg-muted px-1 rounded">{"{{scheduling_link}}"}</code></p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="google_calendar">Google Calendar Appointment Scheduling</Label>
                      <Input
                        id="google_calendar"
                        placeholder="https://calendar.google.com/calendar/appointments/..."
                        value={schedulingLinks.google_calendar}
                        onChange={(e) => setSchedulingLinks({ ...schedulingLinks, google_calendar: e.target.value })}
                        data-testid="google-calendar-input"
                      />
                      <p className="text-xs text-muted-foreground">Available as <code className="bg-muted px-1 rounded">{"{{calendar_link}}"}</code></p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="square">Square Appointments</Label>
                      <Input
                        id="square"
                        placeholder="https://squareup.com/appointments/..."
                        value={schedulingLinks.square_appointments}
                        onChange={(e) => setSchedulingLinks({ ...schedulingLinks, square_appointments: e.target.value })}
                        data-testid="square-input"
                      />
                      <p className="text-xs text-muted-foreground">Available as <code className="bg-muted px-1 rounded">{"{{square_link}}"}</code></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* SECTION 2: Documents - REDESIGNED v2 (Per-Client Upload Model) */}
              <Card id="documents-section" className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-amber-500" />
                    Documents
                    {documentHandling.method && documentHandling.method !== "none" ? (
                      <Badge className="bg-green-100 text-green-700 text-xs font-normal ml-auto">Configured</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-xs font-normal ml-auto">Not set</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>How do clients send you documents?</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Document Handling Method - REDESIGNED v2 */}
                  <div className="space-y-3">
                    <div className="grid gap-3 max-w-lg">
                      {[
                        { 
                          value: "portal", 
                          label: "HeyIntake Upload Portal", 
                          desc: "Clients upload documents securely inside HeyIntake",
                          badge: "Recommended",
                          badgeColor: "bg-primary/10 text-primary"
                        },
                        { 
                          value: "email", 
                          label: "Business Email", 
                          desc: "Clients send documents by replying to your email",
                          badge: null
                        },
                        { 
                          value: "client_specific", 
                          label: "Client-specific upload link", 
                          desc: "You provide an upload link individually for each client (e.g., Google Drive, Dropbox)",
                          badge: null
                        },
                        { 
                          value: "none", 
                          label: "Not Needed", 
                          desc: "You don't collect documents from clients",
                          badge: null
                        }
                      ].map((option) => (
                        <label 
                          key={option.value} 
                          className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                            documentHandling.method === option.value 
                              ? 'border-primary bg-primary/5 shadow-sm' 
                              : 'hover:bg-muted/30'
                          }`}
                        >
                          <input 
                            type="radio" 
                            name="doc_method" 
                            value={option.value}
                            checked={documentHandling.method === option.value}
                            onChange={(e) => setDocumentHandling({ ...documentHandling, method: e.target.value })}
                            className="w-4 h-4 text-primary"
                            data-testid={`doc-method-${option.value}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{option.label}</p>
                              {option.badge && (
                                <Badge className={`${option.badgeColor} text-xs font-normal`}>{option.badge}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{option.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {documentHandling.method === "email" && (
                    <div className="space-y-2 max-w-lg pl-4 border-l-2 border-primary/20">
                      <Label>Document Submission Email *</Label>
                      <Input
                        type="email"
                        placeholder="documents@yourbusiness.com"
                        value={documentHandling.email || ""}
                        onChange={(e) => setDocumentHandling({ ...documentHandling, email: e.target.value })}
                        data-testid="doc-email-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Clients will be instructed to send documents to this email address.
                      </p>
                    </div>
                  )}
                  
                  {documentHandling.method === "portal" && (
                    <div className="max-w-lg pl-4 border-l-2 border-primary/20">
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <p className="text-sm text-green-700">
                          Clients will upload documents directly through your secure HeyIntake portal.
                          You can also add client-specific upload links in each submission if needed.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {documentHandling.method === "client_specific" && (
                    <div className="max-w-lg pl-4 border-l-2 border-primary/20">
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                        <p className="text-sm text-amber-800">
                          <strong>Per-client links:</strong> Add a unique upload link (Google Drive, Dropbox, etc.) 
                          for each client in the Submissions tab using the "External Upload Link" field.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {documentHandling.method !== "none" && (
                    <div className="space-y-2 max-w-lg">
                      <Label>Additional Instructions (optional)</Label>
                      <Textarea
                        placeholder="e.g., Please include your name in the file name..."
                        value={documentHandling.instructions || ""}
                        onChange={(e) => setDocumentHandling({ ...documentHandling, instructions: e.target.value })}
                        className="h-20 resize-none"
                        maxLength={1000}
                        data-testid="doc-instructions-input"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* SECTION 3: Agreement - NEW DEDICATED SECTION */}
              <Card id="agreement-section" className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all" data-testid="agreement-settings-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500" />
                    Agreement
                    {agreementSettings.enabled ? (
                      <Badge className="bg-green-100 text-green-700 text-xs font-normal ml-auto">Configured</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-xs font-normal ml-auto">Not set</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Terms and conditions clients must accept before submitting</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Agreement Toggle */}
                  <div className="flex items-center justify-between max-w-lg">
                    <div>
                      <p className="font-medium">Enable Agreement Requirement</p>
                      <p className="text-sm text-muted-foreground">Clients must accept your terms before submitting</p>
                    </div>
                    <Switch
                      checked={agreementSettings.enabled}
                      onCheckedChange={(checked) => setAgreementSettings({ ...agreementSettings, enabled: checked })}
                      data-testid="agreement-enabled-toggle"
                    />
                  </div>
                  
                  {agreementSettings.enabled && (
                    <>
                      {/* Agreement Mode Selection */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Agreement Content</Label>
                        <div className="grid gap-3 max-w-lg">
                          <label 
                            className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                              agreementSettings.mode === "link" 
                                ? 'border-primary bg-primary/5 shadow-sm' 
                                : 'hover:bg-muted/30'
                            }`}
                          >
                            <input 
                              type="radio" 
                              name="agreement_mode" 
                              value="link"
                              checked={agreementSettings.mode === "link"}
                              onChange={() => setAgreementSettings({ ...agreementSettings, mode: "link" })}
                              className="w-4 h-4 text-primary"
                              data-testid="agreement-mode-link"
                            />
                            <div className="flex items-center gap-3">
                              <ExternalLink className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="font-medium">Link to agreement</p>
                                <p className="text-sm text-muted-foreground">Use an existing document URL</p>
                              </div>
                            </div>
                          </label>
                          
                          <label 
                            className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                              agreementSettings.mode === "text" 
                                ? 'border-primary bg-primary/5 shadow-sm' 
                                : 'hover:bg-muted/30'
                            }`}
                          >
                            <input 
                              type="radio" 
                              name="agreement_mode" 
                              value="text"
                              checked={agreementSettings.mode === "text"}
                              onChange={() => setAgreementSettings({ ...agreementSettings, mode: "text" })}
                              className="w-4 h-4 text-primary"
                              data-testid="agreement-mode-text"
                            />
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="font-medium">Paste agreement text</p>
                                <p className="text-sm text-muted-foreground">Enter your terms directly</p>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {/* Link Input (when mode is "link") */}
                      {agreementSettings.mode === "link" && (
                        <div className="space-y-2 max-w-lg pl-4 border-l-2 border-primary/20">
                          <Label htmlFor="agreement_url">Agreement Link</Label>
                          <Input
                            id="agreement_url"
                            placeholder="https://your-site.com/terms-of-service"
                            value={agreementSettings.agreement_url || ""}
                            onChange={(e) => setAgreementSettings({ ...agreementSettings, agreement_url: e.target.value })}
                            data-testid="agreement-url-input"
                          />
                          <p className="text-xs text-muted-foreground">Must be a public HTTPS URL</p>
                        </div>
                      )}
                      
                      {/* Text Input (when mode is "text") */}
                      {agreementSettings.mode === "text" && (
                        <div className="space-y-2 max-w-lg pl-4 border-l-2 border-primary/20">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="agreement_text">Agreement Text</Label>
                            {agreementSettings.agreement_text && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowAgreementPreview(true)}
                                className="text-xs"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Preview
                              </Button>
                            )}
                          </div>
                          <Textarea
                            id="agreement_text"
                            placeholder="Enter your terms and conditions here...&#10;&#10;By submitting this form, you agree to..."
                            value={agreementSettings.agreement_text || ""}
                            onChange={(e) => setAgreementSettings({ ...agreementSettings, agreement_text: e.target.value })}
                            className="min-h-[200px] font-mono text-sm"
                            data-testid="agreement-text-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            {(agreementSettings.agreement_text?.length || 0).toLocaleString()} / 50,000 characters
                          </p>
                        </div>
                      )}
                      
                      {/* Optional Instructions */}
                      <div className="space-y-2 max-w-lg">
                        <Label htmlFor="agreement_instructions">Instructions (optional)</Label>
                        <Input
                          id="agreement_instructions"
                          placeholder="e.g., Please review our terms carefully before proceeding"
                          value={agreementSettings.instructions || ""}
                          onChange={(e) => setAgreementSettings({ ...agreementSettings, instructions: e.target.value })}
                          data-testid="agreement-instructions-input"
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              
              {/* SECTION 4: Signing (Contracts) */}
              <Card id="signing-section" className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-violet-500" />
                    Contract Signing
                    {signingSettings.signwell_connected || signingSettings.signing_link ? (
                      <Badge className="bg-green-100 text-green-700 text-xs font-normal ml-auto">Configured</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-xs font-normal ml-auto">Not set</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>E-signature solution for legally binding contracts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Signing Mode Selection */}
                  <div className="space-y-3">
                    <div className="grid gap-3 max-w-lg">
                      <label 
                        className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                          signingSettings.mode === "link" 
                            ? 'border-primary bg-primary/5 shadow-sm' 
                            : 'hover:bg-muted/30'
                        }`}
                      >
                        <input 
                          type="radio" 
                          name="signing_mode" 
                          value="link"
                          checked={signingSettings.mode === "link"}
                          onChange={() => setSigningSettings({ ...signingSettings, mode: "link" })}
                          className="w-4 h-4 text-primary"
                          data-testid="signing-mode-link"
                        />
                        <div>
                          <p className="font-medium">Use my existing signing link</p>
                          <p className="text-sm text-muted-foreground">Paste a link to your signing service</p>
                        </div>
                      </label>
                      
                      <label 
                        className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                          signingSettings.mode === "provider" 
                            ? 'border-primary bg-primary/5 shadow-sm' 
                            : 'hover:bg-muted/30'
                        }`}
                      >
                        <input 
                          type="radio" 
                          name="signing_mode" 
                          value="provider"
                          checked={signingSettings.mode === "provider"}
                          onChange={() => setSigningSettings({ ...signingSettings, mode: "provider" })}
                          className="w-4 h-4 text-primary"
                          data-testid="signing-mode-provider"
                        />
                        <div>
                          <p className="font-medium">Connect a signing provider</p>
                          <p className="text-sm text-muted-foreground">Recommended for tracking and automation</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Simple Link Input */}
                  {signingSettings.mode === "link" && (
                    <div className="space-y-2 max-w-lg pl-4 border-l-2 border-primary/20">
                      <Label htmlFor="signing_link">External Signing Link</Label>
                      <Input
                        id="signing_link"
                        placeholder="https://app.docusign.com/..."
                        value={signingSettings.signing_link || ""}
                        onChange={(e) => setSigningSettings({ ...signingSettings, signing_link: e.target.value })}
                        data-testid="signing-link-input"
                      />
                      <p className="text-xs text-muted-foreground">Available as <code className="bg-muted px-1 rounded">{"{{agreement_link}}"}</code></p>
                    </div>
                  )}
                  
                  {/* Provider Connection */}
                  {signingSettings.mode === "provider" && (
                    <div className="space-y-4 max-w-lg pl-4 border-l-2 border-primary/20">
                      {/* SignWell */}
                      <div className="border rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <FileSignature className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium">SignWell</p>
                              <p className="text-xs text-muted-foreground">Simple e-signature solution</p>
                            </div>
                          </div>
                          {signingSettings.signwell_connected ? (
                            <Badge className="bg-green-100 text-green-700">Connected</Badge>
                          ) : (
                            <Badge variant="outline">Not connected</Badge>
                          )}
                        </div>
                        
                        {/* Pricing transparency notice */}
                        <p className="text-xs text-muted-foreground mb-3 bg-slate-50 p-2 rounded">
                          E-signatures are powered by your SignWell account. Usage may be subject to SignWell's pricing.
                        </p>
                        
                        {/* SignWell Connection UI - 3 states: connected, replacing, disconnected */}
                        {signingSettings.signwell_connected && !signwellReplaceMode ? (
                          // STATE: Connected - show masked key, disconnect + replace buttons
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Input
                                type="text"
                                value="••••••••••••••••"
                                readOnly
                                className="bg-slate-50 text-sm max-w-[200px] cursor-not-allowed"
                              />
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSignwellReplaceMode(true);
                                  setSignwellApiKey("");
                                }}
                                disabled={savingSigningSettings}
                              >
                                Replace key
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => disconnectSigningProvider("signwell")}
                                disabled={savingSigningSettings}
                                data-testid="disconnect-signwell"
                              >
                                Disconnect
                              </Button>
                            </div>
                            <p className="text-xs text-green-600">API key validated and saved</p>
                          </div>
                        ) : signwellReplaceMode ? (
                          // STATE: Replacing - editable input, save new key button
                          <div className="space-y-3">
                            <div className="space-y-2">
                              <Label>New SignWell API Key</Label>
                              <Input
                                type="password"
                                placeholder="Paste your new SignWell API key..."
                                value={signwellApiKey}
                                onChange={(e) => setSignwellApiKey(e.target.value)}
                                data-testid="signwell-api-key-input"
                                autoFocus
                              />
                              <p className="text-xs text-muted-foreground">
                                Find your API key in SignWell → Settings → API
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSignwellReplaceMode(false);
                                  setSignwellApiKey("");
                                }}
                                disabled={savingSigningSettings}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={async () => {
                                  if (!signwellApiKey.trim()) {
                                    toast.error("Please enter an API key");
                                    return;
                                  }
                                  setSavingSigningSettings(true);
                                  try {
                                    await axios.put(`${API}/business/signing-settings`, {
                                      signwell_api_key: signwellApiKey.trim()
                                    }, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setSigningSettings(prev => ({ ...prev, signwell_connected: true, provider: "signwell", mode: "provider" }));
                                    setSignwellApiKey("");
                                    setSignwellReplaceMode(false);
                                    toast.success("SignWell API key updated");
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || "Invalid API key. Please check and try again.");
                                  } finally {
                                    setSavingSigningSettings(false);
                                  }
                                }}
                                disabled={savingSigningSettings || !signwellApiKey.trim()}
                              >
                                {savingSigningSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Save new key
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // STATE: Not connected - editable input, connect button
                          <div className="space-y-3">
                            <div className="space-y-2">
                              <Label>SignWell API Key</Label>
                              <Input
                                type="password"
                                placeholder="Paste your SignWell API key..."
                                value={signwellApiKey}
                                onChange={(e) => setSignwellApiKey(e.target.value)}
                                data-testid="signwell-api-key-input"
                              />
                              <p className="text-xs text-muted-foreground">
                                Find your API key in SignWell → Settings → API
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={async () => {
                                if (!signwellApiKey.trim()) {
                                  toast.error("Please enter an API key");
                                  return;
                                }
                                setSavingSigningSettings(true);
                                try {
                                  await axios.put(`${API}/business/signing-settings`, {
                                    signwell_api_key: signwellApiKey.trim()
                                  }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  setSigningSettings(prev => ({ ...prev, signwell_connected: true, provider: "signwell", mode: "provider" }));
                                  setSignwellApiKey("");
                                  toast.success("SignWell connected successfully");
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Invalid API key. Please check and try again.");
                                } finally {
                                  setSavingSigningSettings(false);
                                }
                              }}
                              disabled={savingSigningSettings || !signwellApiKey.trim()}
                            >
                              {savingSigningSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                              Connect
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* DocuSign (Scaffold) */}
                      <div className="border rounded-xl p-4 bg-slate-50/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                              <FileSignature className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                              <p className="font-medium">DocuSign</p>
                              <p className="text-xs text-muted-foreground">Enterprise e-signature</p>
                            </div>
                          </div>
                          {signingSettings.docusign_connected ? (
                            <Badge className="bg-green-100 text-green-700">Connected</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Coming Soon</Badge>
                          )}
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled
                          className="opacity-60"
                          data-testid="connect-docusign"
                        >
                          Connect DocuSign
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2">
                          DocuSign integration requires OAuth connection. Coming soon.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* SECTION 4: Integrations & Webhooks - REDESIGNED */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-orange-500" />
                    Integrations & Webhooks
                    {webhookSettings.webhook_url ? (
                      <Badge className="bg-green-100 text-green-700 text-xs font-normal ml-auto">Configured</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-xs font-normal ml-auto">Optional</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Connect your tools to automatically send leads and client data to CRMs, project management apps, and more.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Setup Guides */}
                  <div className="space-y-3">
                    <Label>Setup Guides</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
                      <a 
                        href="https://zapier.com/apps/webhook/help" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border rounded-xl hover:bg-orange-50 hover:border-orange-200 transition-colors"
                      >
                        <img src="https://cdn.simpleicons.org/zapier/FF4A00" alt="Zapier" className="w-6 h-6" />
                        <div>
                          <p className="text-sm font-medium">Zapier</p>
                          <p className="text-xs text-muted-foreground">Paid plans only</p>
                        </div>
                      </a>
                      <a 
                        href="https://www.make.com/en/help/tools/webhooks" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border rounded-xl hover:bg-purple-50 hover:border-purple-200 transition-colors"
                      >
                        <img src="https://cdn.simpleicons.org/make/6D00CC" alt="Make" className="w-6 h-6" />
                        <div>
                          <p className="text-sm font-medium">Make</p>
                          <p className="text-xs text-muted-foreground">Free tier available</p>
                        </div>
                      </a>
                      <a 
                        href="https://www.pabbly.com/connect/integrations/webhook/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-colors"
                      >
                        <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-bold">P</div>
                        <div>
                          <p className="text-sm font-medium">Pabbly</p>
                          <p className="text-xs text-muted-foreground">One-time pricing</p>
                        </div>
                      </a>
                    </div>
                  </div>
                  
                  {/* Webhook URL */}
                  <div className="space-y-2 max-w-lg">
                    <Label htmlFor="webhook_url">Webhook URL</Label>
                    <Input
                      id="webhook_url"
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      value={webhookSettings.webhook_url}
                      onChange={(e) => setWebhookSettings({ ...webhookSettings, webhook_url: e.target.value })}
                      data-testid="webhook-url-input"
                    />
                    <p className="text-xs text-muted-foreground">Paste your webhook URL from Zapier, Make, Pabbly, or any other webhook service.</p>
                  </div>
                  
                  {/* Event Selection */}
                  <div className="space-y-3 max-w-lg">
                    <Label>Events to Send</Label>
                    <div className="space-y-2">
                      {[
                        { key: "new_submission", label: "New Submission", desc: "When a new lead submits a form" },
                        { key: "status_changed", label: "Status Changed", desc: "When a lead's status is updated" },
                        { key: "intake_completed", label: "Intake Completed", desc: "When a client completes their intake" }
                      ].map((event) => (
                        <label key={event.key} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                          <input 
                            type="checkbox" 
                            checked={webhookSettings.events[event.key] || false}
                            onChange={(e) => setWebhookSettings({
                              ...webhookSettings,
                              events: { ...webhookSettings.events, [event.key]: e.target.checked }
                            })}
                            className="w-4 h-4 text-primary rounded"
                            data-testid={`webhook-event-${event.key}`}
                          />
                          <div>
                            <p className="font-medium text-sm">{event.label}</p>
                            <p className="text-xs text-muted-foreground">{event.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Test Button Only */}
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline"
                      onClick={testWebhook} 
                      disabled={testingWebhook || !webhookSettings.webhook_url}
                      className="rounded-full"
                      data-testid="test-webhook-btn"
                    >
                      {testingWebhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Test Webhook
                    </Button>
                  </div>
                  
                  {/* Info note */}
                  <div className="text-xs text-muted-foreground max-w-lg bg-slate-50 p-3 rounded-lg">
                    <strong>Note:</strong> Integrations are optional. HeyIntake works great on its own. 
                    Connect your tools only if you want to sync data with other systems.
                  </div>
                </CardContent>
              </Card>
              
              {/* Other Standard Links */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5" />
                    Other Links
                  </CardTitle>
                  <CardDescription>Additional links you can use in email templates and automations.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 max-w-lg">
                    {STANDARD_LINKS.filter(link => link.key !== 'agreement_link').map((link) => (
                      <div key={link.key} className="space-y-2">
                        <Label htmlFor={link.key}>{link.label}</Label>
                        <Input
                          id={link.key}
                          placeholder={link.placeholder}
                          value={standardLinks[link.key] || ""}
                          onChange={(e) => setStandardLinks({ ...standardLinks, [link.key]: e.target.value })}
                          data-testid={`${link.key}-input`}
                        />
                        <p className="text-xs text-muted-foreground">
                          Available as <code className="bg-muted px-1 rounded">{`{{${link.key}}}`}</code>
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Custom Links */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Custom Links
                  </CardTitle>
                  <CardDescription>Add your own custom links to use in templates.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {customLinks.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <LinkIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No custom links yet</p>
                      <p className="text-sm">Add links like portfolio, resources, or any URL you frequently use.</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-w-lg">
                      {customLinks.map((link, index) => (
                        <div key={index} className="flex gap-3 items-start p-4 bg-muted/30 rounded-lg">
                          <div className="flex-1 space-y-3">
                            <div className="space-y-2">
                              <Label>Label (will become variable name)</Label>
                              <Input
                                placeholder="e.g., Portfolio Link"
                                value={link.label}
                                onChange={(e) => updateCustomLink(index, 'label', e.target.value)}
                                data-testid={`custom-link-label-${index}`}
                              />
                              {link.label && (
                                <p className="text-xs text-muted-foreground">
                                  Available as <code className="bg-muted px-1 rounded">{`{{${link.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}}}`}</code>
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label>URL</Label>
                              <Input
                                placeholder="https://..."
                                value={link.url}
                                onChange={(e) => updateCustomLink(index, 'url', e.target.value)}
                                data-testid={`custom-link-url-${index}`}
                              />
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeCustomLink(index)}
                            className="text-muted-foreground hover:text-destructive"
                            data-testid={`remove-custom-link-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    onClick={addCustomLink}
                    className="rounded-full"
                    data-testid="add-custom-link-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Link
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* ==================== NOTIFICATIONS TAB ==================== */}
            <TabsContent value="notifications" className="space-y-6">
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Email Notifications
                  </CardTitle>
                  <CardDescription>Configure when and how you receive email notifications.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4 max-w-lg">
                    {/* New Submission */}
                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                      <div>
                        <p className="font-medium">New Submission Alerts</p>
                        <p className="text-sm text-muted-foreground">Get notified when someone submits a form</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.email_new_submission}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, email_new_submission: e.target.checked })}
                          className="sr-only peer"
                          data-testid="notify-new-submission"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    
                    {/* Reminders */}
                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                      <div>
                        <p className="font-medium">Follow-up Reminders</p>
                        <p className="text-sm text-muted-foreground">Get reminded about leads that need attention</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.email_reminders}
                          onChange={(e) => setNotificationSettings({ ...notificationSettings, email_reminders: e.target.checked })}
                          className="sr-only peer"
                          data-testid="notify-reminders"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    
                    {/* Digest */}
                    <div className="p-4 bg-muted/30 rounded-xl space-y-3">
                      <div>
                        <p className="font-medium">Activity Digest</p>
                        <p className="text-sm text-muted-foreground">Receive a summary of all activity</p>
                      </div>
                      <Select 
                        value={notificationSettings.email_digest} 
                        onValueChange={(value) => setNotificationSettings({ ...notificationSettings, email_digest: value })}
                      >
                        <SelectTrigger className="w-full max-w-[200px]" data-testid="digest-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Never</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* ==================== BILLING TAB ==================== */}
            <TabsContent value="billing" className="space-y-6">
              {/* Current Plan Overview */}
              <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Your Plan
                  </CardTitle>
                  <CardDescription>Manage your subscription and billing.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Billing Disabled Banner */}
                  {!billingStatus.enabled && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="billing-disabled-banner">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-amber-800">Billing Unavailable</p>
                          <p className="text-sm text-amber-700 mt-1">{billingStatus.message}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Plan Info Cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Current Plan */}
                    <div className="p-4 bg-muted/30 rounded-xl">
                      <p className="text-sm text-muted-foreground mb-1">Current Plan</p>
                      <p className="text-2xl font-bold text-foreground capitalize" data-testid="billing-plan-name">
                        {subscriptionStatus?.is_inactive 
                          ? "None" 
                          : (subscriptionStatus?.plan_name || user?.subscription_plan || "None")}
                      </p>
                      {subscriptionStatus?.plan_price > 0 && !subscriptionStatus?.is_inactive && (
                        <p className="text-sm text-muted-foreground">
                          {(subscriptionStatus?.billing_cadence === 'annual' || subscriptionStatus?.billingCadence === 'annual')
                            ? `$${Math.round(subscriptionStatus.plan_price * 12 * 0.8)}/year`
                            : `$${subscriptionStatus.plan_price}/month`}
                        </p>
                      )}
                    </div>
                    
                    {/* Limits Reset / Status */}
                    <div className="p-4 bg-muted/30 rounded-xl">
                      <p className="text-sm text-muted-foreground mb-1">
                        {subscriptionStatus?.pending_downgrade 
                          ? "Downgrade Date" 
                          : subscriptionStatus?.cancel_at_period_end 
                            ? "Access Until" 
                            : "Limits reset on"}
                      </p>
                      {subscriptionStatus?.is_inactive ? (
                        <p className="text-2xl font-bold text-foreground">N/A</p>
                      ) : (
                        <>
                          <p className="text-2xl font-bold text-foreground flex items-center gap-2" data-testid="next-billing-date">
                            <Calendar className="w-5 h-5 text-muted-foreground" />
                            {formatDate(
                              subscriptionStatus?.pending_downgrade 
                                ? subscriptionStatus?.pending_downgrade_effective_at 
                                : (subscriptionStatus?.next_billing_date || subscriptionStatus?.intro_end_at || subscriptionStatus?.cycle_end_at)
                            )}
                          </p>
                          {subscriptionStatus?.pending_downgrade ? (
                            <p className="text-sm text-blue-600 mt-1">Downgrading to {subscriptionStatus?.pending_downgrade_to}</p>
                          ) : subscriptionStatus?.cancel_at_period_end && (
                            <p className="text-sm text-orange-600 mt-1">Cancellation scheduled</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Access Status */}
                  {!subscriptionStatus?.is_inactive && (
                    <div className="p-4 bg-muted/30 rounded-xl space-y-4" data-testid="access-status-section">
                      {/* Status Badge */}
                      <div className="flex items-center gap-3">
                        <Badge 
                          className={`px-3 py-1 ${
                            (subscriptionStatus?.is_trialing || subscriptionStatus?.is_intro) 
                              ? 'bg-blue-100 text-blue-700'
                              : subscriptionStatus?.subscription_status === "active"
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                          }`}
                          data-testid="access-status-badge"
                        >
                          {(subscriptionStatus?.is_trialing || subscriptionStatus?.is_intro) 
                            ? `Trial – ${subscriptionStatus?.plan_name || "Plan"}`
                            : subscriptionStatus?.subscription_status === "active"
                              ? `Active – ${subscriptionStatus?.plan_name}`
                              : "Payment Due"
                          }
                        </Badge>
                        {(subscriptionStatus?.is_trialing || subscriptionStatus?.is_intro) && (
                          <span className="text-sm text-muted-foreground">
                            {subscriptionStatus?.trial_days_remaining || subscriptionStatus?.intro_days_remaining || 0} days remaining
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Usage Overview - SLICE 5 */}
                  {!subscriptionStatus?.is_inactive && usage && (
                    <Card className="border shadow-none" data-testid="usage-overview-section">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Usage This Period
                        </CardTitle>
                        {usage?.billing_cycle_start && (
                          <CardDescription>
                            Resets {(() => {
                              const nextBilling = subscriptionStatus?.next_billing_date || subscriptionStatus?.cycle_end_at;
                              if (!nextBilling) return "at next billing cycle";
                              const resetDate = new Date(nextBilling);
                              const now = new Date();
                              const daysUntil = Math.ceil((resetDate - now) / (1000 * 60 * 60 * 24));
                              if (daysUntil <= 0) return "soon";
                              if (daysUntil === 1) return "tomorrow";
                              return `in ${daysUntil} days`;
                            })()}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Forms Usage */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Forms</span>
                            <span className="text-sm text-muted-foreground" data-testid="forms-usage">
                              {usage?.forms_count || 0} / {usage?.forms_limit === -1 ? "Unlimited" : usage?.forms_limit || 3}
                            </span>
                          </div>
                          <Progress 
                            value={usage?.forms_percentage || 0} 
                            className={`h-2 ${usage?.forms_percentage >= 80 ? '[&>div]:bg-amber-500' : ''} ${usage?.forms_percentage >= 100 ? '[&>div]:bg-red-500' : ''}`}
                          />
                          {usage?.forms_percentage >= 80 && usage?.forms_percentage < 100 && (
                            <p className="text-xs text-amber-600">Approaching limit</p>
                          )}
                          {usage?.forms_percentage >= 100 && (
                            <p className="text-xs text-red-600">Limit reached - upgrade to create more forms</p>
                          )}
                        </div>
                        
                        {/* Submissions Usage */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Submissions This Month</span>
                            <span className="text-sm text-muted-foreground" data-testid="submissions-usage">
                              {(subscriptionStatus?.is_trialing || subscriptionStatus?.is_intro) 
                                ? `${subscriptionStatus?.trial_leads_used || 0} / ${subscriptionStatus?.trial_leads_limit || 20}`
                                : `${usage?.monthly_leads_used || 0} / ${usage?.monthly_leads_limit === -1 ? "Unlimited" : usage?.monthly_leads_limit || 100}`
                              }
                            </span>
                          </div>
                          <Progress 
                            value={usage?.leads_percentage || 0} 
                            className={`h-2 ${usage?.leads_percentage >= 80 ? '[&>div]:bg-amber-500' : ''} ${usage?.leads_percentage >= 100 ? '[&>div]:bg-red-500' : ''}`}
                          />
                          {usage?.leads_percentage >= 80 && usage?.leads_percentage < 100 && (
                            <p className="text-xs text-amber-600">Approaching limit</p>
                          )}
                          {usage?.leads_percentage >= 100 && (
                            <p className="text-xs text-red-600">Limit reached - upgrade to accept more submissions</p>
                          )}
                        </div>
                        
                        {/* Warnings Display */}
                        {usage?.has_limits_reached && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
                            <div className="flex items-center gap-2 text-red-800">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">Limits Reached</span>
                            </div>
                            <ul className="mt-2 space-y-1">
                              {usage?.limits_reached?.map((limit, i) => (
                                <li key={i} className="text-sm text-red-600">{limit.message}</li>
                              ))}
                            </ul>
                            <Button 
                              size="sm" 
                              className="mt-3 bg-red-600 hover:bg-red-700"
                              onClick={() => setBillingCadence("annual")}
                            >
                              <Zap className="w-4 h-4 mr-1" />
                              Upgrade Now
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  
                  <Separator />
                  
                  {/* Manage Subscription Actions */}
                  {(subscriptionStatus?.subscription_status === "active" || subscriptionStatus?.is_trialing || subscriptionStatus?.is_intro) && (
                    <div className="space-y-4">
                      {/* Change Plan CTA */}
                      {!subscriptionStatus?.is_inactive && (
                        <div className="flex items-center justify-between p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
                          <div>
                            <p className="font-medium text-zinc-900">Change Plan</p>
                            <p className="text-sm text-zinc-500">Upgrades take effect immediately. Downgrades apply at the end of your billing cycle.</p>
                          </div>
                          <Button
                            onClick={() => setShowChangePlan(true)}
                            className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0"
                            disabled={loading || subscriptionStatus?.cancel_at_period_end}
                            data-testid="change-plan-btn"
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            Change Plan
                          </Button>
                        </div>
                      )}
                      
                      {/* Cancel / Reactivate */}
                      <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">Manage Subscription</p>
                        <p className="text-sm text-muted-foreground">
                          {subscriptionStatus?.cancel_at_period_end 
                            ? "Your subscription will end at the end of the billing period."
                            : "Cancel anytime. You'll retain access until the end of your period."
                          }
                        </p>
                      </div>
                      {subscriptionStatus?.cancel_at_period_end ? (
                        <Button
                          variant="outline"
                          onClick={handleReactivateSubscription}
                          disabled={loading}
                          className="rounded-full"
                          data-testid="reactivate-btn"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reactivate"}
                        </Button>
                      ) : (
                        <AlertDialog open={showCancelFeedback} onOpenChange={setShowCancelFeedback}>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="rounded-full text-muted-foreground hover:text-destructive"
                              onClick={openCancelFeedback}
                            >
                              Cancel Subscription
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="max-w-md">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Your subscription will be canceled at the end of the current billing period. You'll retain full access until then.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>Why are you canceling? (optional)</Label>
                                <Select value={cancelReason} onValueChange={setCancelReason}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a reason..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="too_expensive">Too expensive</SelectItem>
                                    <SelectItem value="not_using">Not using it enough</SelectItem>
                                    <SelectItem value="missing_features">Missing features I need</SelectItem>
                                    <SelectItem value="found_alternative">Found a better alternative</SelectItem>
                                    <SelectItem value="technical_issues">Technical issues</SelectItem>
                                    <SelectItem value="temporary">Just testing / temporary</SelectItem>
                                    <SelectItem value="business_closed">Business closed or paused</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Any additional feedback? (optional)</Label>
                                <textarea
                                  value={cancelComment}
                                  onChange={(e) => setCancelComment(e.target.value)}
                                  placeholder="Help us improve..."
                                  className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                              </div>
                            </div>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={handleCancelSubscription}
                                className="bg-destructive hover:bg-destructive/90"
                                data-testid="confirm-cancel-btn"
                              >
                                {cancelingSubscription ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, Cancel"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Available Plans - For Upgrades (show for non-professional plans) */}
              {(() => {
                // Debug: check conditions
                const isNotInactive = !subscriptionStatus?.is_inactive;
                const isStarter = 
                  subscriptionStatus?.subscription_plan === "starter" || 
                  subscriptionStatus?.plan_name?.toLowerCase() === "starter" ||
                  planName?.toLowerCase() === "starter" ||
                  user?.subscription_plan === "starter";
                const shouldShowUpgrade = isNotInactive && isStarter;
                
                if (!shouldShowUpgrade) return null;
                
                return (
                <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]" data-testid="available-plans-section">
                  <CardHeader>
                    <CardTitle>Available Plans</CardTitle>
                    <CardDescription>Compare plans and upgrade anytime.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Billing Toggle */}
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex items-center bg-muted/50 rounded-full p-1">
                        <button
                          onClick={() => setBillingCadence("monthly")}
                          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                            billingCadence === "monthly" 
                              ? "bg-primary text-white shadow-sm" 
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          data-testid="billing-toggle-monthly"
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => setBillingCadence("annual")}
                          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors flex items-center gap-2 ${
                            billingCadence === "annual" 
                              ? "bg-primary text-white shadow-sm" 
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                          data-testid="billing-toggle-annual"
                        >
                          Annual
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Save 20%</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Plans Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {PLANS.map((plan) => {
                        const isCurrentPlan = (
                          subscriptionStatus?.subscription_plan === plan.id || 
                          subscriptionStatus?.plan_name?.toLowerCase() === plan.name.toLowerCase() ||
                          planName?.toLowerCase() === plan.name.toLowerCase() ||
                          user?.subscription_plan === plan.id
                        );
                        
                        return (
                          <div 
                            key={plan.id}
                            className={`relative p-6 rounded-2xl border-2 ${
                              isCurrentPlan 
                                ? 'border-green-500 bg-green-50/30' 
                                : plan.popular 
                                  ? 'border-primary bg-primary/5' 
                                  : 'border-border'
                            }`}
                            data-testid={`plan-card-${plan.id}`}
                          >
                            {isCurrentPlan && (
                              <Badge className="absolute -top-3 left-4 bg-green-600 text-white">
                                Current Plan
                              </Badge>
                            )}
                            {!isCurrentPlan && plan.popular && (
                              <Badge className="absolute -top-3 left-4 bg-primary text-white">
                                Most Popular
                              </Badge>
                            )}
                            <h3 className="text-xl font-bold">{plan.name}</h3>
                            <div className="mt-2">
                              <span className="text-3xl font-bold">
                                ${billingCadence === "annual" 
                                  ? Math.round(plan.annualPrice / 12)
                                  : plan.monthlyPrice}
                              </span>
                              <span className="text-muted-foreground">/mo</span>
                            </div>
                            {billingCadence === "annual" && (
                              <p className="text-sm text-muted-foreground">${plan.annualPrice}/year (billed annually)</p>
                            )}
                            <p className="text-sm text-muted-foreground mt-1">7-day intro for $1</p>
                            
                            <div className="mt-4 space-y-1">
                              <p className="font-medium">{plan.forms}</p>
                              <p className="text-sm text-muted-foreground">{plan.submissions}</p>
                            </div>
                            
                            <ul className="mt-4 space-y-2">
                              {plan.features.map((feature, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm">
                                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            
                            {isCurrentPlan ? (
                              <Button 
                                className="w-full mt-6 rounded-full"
                                variant="outline"
                                disabled
                                data-testid={`current-plan-btn-${plan.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Current Plan
                              </Button>
                            ) : (
                              <Button 
                                className="w-full mt-6 rounded-full bg-primary hover:bg-primary/90"
                                onClick={() => handleUpgradePlan(plan.id)}
                                disabled={loading || !billingStatus.enabled}
                                data-testid={`upgrade-btn-${plan.id}`}
                              >
                                {loading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Zap className="w-4 h-4 mr-2" />
                                    Upgrade Now
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    <p className="text-center text-sm text-muted-foreground mt-6">
                      All plans include a 7-day trial for $1. Auto-renews at full price unless canceled.
                    </p>
                  </CardContent>
                </Card>
                );
              })()}
              
              {/* Plans Comparison (for NEW users without subscription) */}
              {subscriptionStatus?.is_inactive && (
                <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                  <CardHeader>
                    <CardTitle>Choose a Plan</CardTitle>
                    <CardDescription>Start your 7-day trial for just $1</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Billing Toggle */}
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex items-center bg-muted/50 rounded-full p-1">
                        <button
                          onClick={() => setBillingCadence("monthly")}
                          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                            billingCadence === "monthly" 
                              ? "bg-white shadow-sm text-foreground" 
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => setBillingCadence("annual")}
                          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors flex items-center gap-2 ${
                            billingCadence === "annual" 
                              ? "bg-white shadow-sm text-foreground" 
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          Annual
                          <Badge className="bg-green-100 text-green-700 text-xs">Save 20%</Badge>
                        </button>
                      </div>
                    </div>
                    
                    {/* Plans Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {PLANS.map((plan) => (
                        <div 
                          key={plan.id}
                          className={`relative p-6 rounded-2xl border-2 ${
                            plan.popular ? 'border-primary bg-primary/5' : 'border-border'
                          }`}
                        >
                          {plan.popular && (
                            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white">
                              Most Popular
                            </Badge>
                          )}
                          <h3 className="text-xl font-bold">{plan.name}</h3>
                          <div className="mt-2">
                            <span className="text-3xl font-bold">
                              ${billingCadence === "annual" 
                                ? Math.round(plan.annualPrice / 12)
                                : plan.monthlyPrice}
                            </span>
                            <span className="text-muted-foreground">/month</span>
                          </div>
                          {billingCadence === "annual" && (
                            <p className="text-sm text-green-600 mt-1">
                              ${plan.annualPrice}/year (save ${plan.monthlyPrice * 12 - plan.annualPrice})
                            </p>
                          )}
                          <ul className="mt-4 space-y-2">
                            {plan.features.map((feature, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                          <Button 
                            className="w-full mt-6 rounded-full"
                            variant={plan.popular ? "default" : "outline"}
                            onClick={() => handleStartTrial(plan.id)}
                            disabled={loading || !billingStatus.enabled}
                            data-testid={`start-trial-${plan.id}`}
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start $1 Trial"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
          </div>
        </div>
      </main>
      
      {/* Agreement Text Preview Modal */}
      <Dialog open={showAgreementPreview} onOpenChange={setShowAgreementPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="agreement-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5" />
              Agreement Preview
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[50vh] pr-4">
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-gray-700">
              {agreementSettings.agreement_text || "No agreement text entered."}
            </div>
          </ScrollArea>
          <div className="flex justify-end pt-4 border-t">
            <Button 
              onClick={() => setShowAgreementPreview(false)}
              data-testid="close-agreement-preview-btn"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Change Plan Modal ═══ */}
      <Dialog open={showChangePlan} onOpenChange={setShowChangePlan}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Change Plan</DialogTitle>
            <DialogDescription>Upgrades take effect immediately with prorated billing. Downgrades apply at the end of your billing cycle.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { id: "starter", name: "Starter", monthlyPrice: 19, annualPrice: 16, forms: "3 forms", submissions: "100 submissions/mo", features: ["AI lead scoring", "Email templates", "Built-in CRM", "Automations"] },
              { id: "professional", name: "Professional", monthlyPrice: 49, annualPrice: 39, forms: "10 forms", submissions: "500 submissions/mo", features: ["Everything in Starter", "Custom branding", "E-signature", "Document collection", "Priority support"] }
            ].map((plan) => {
              const currentPlan = (subscriptionStatus?.subscription_plan || user?.subscription_plan || "starter").toLowerCase();
              const isCurrent = currentPlan === plan.id;
              const isUpgrade = plan.id === "professional" && currentPlan === "starter";
              const isDowngrade = plan.id === "starter" && currentPlan === "professional";
              const price = billingCadence === "annual" ? plan.annualPrice : plan.monthlyPrice;
              
              return (
                <div key={plan.id} className={`p-5 rounded-xl border-2 transition-all ${isCurrent ? "border-indigo-200 bg-indigo-50/50" : "border-zinc-200 hover:border-zinc-300"}`} data-testid={`plan-option-${plan.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg text-zinc-900">{plan.name}</h3>
                        {isCurrent && <span className="text-xs font-medium text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">Current Plan</span>}
                      </div>
                      <p className="text-sm text-zinc-500">{plan.forms} · {plan.submissions}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-zinc-900">${price}<span className="text-sm font-normal text-zinc-400">/mo</span></p>
                    </div>
                  </div>
                  <ul className="space-y-1 mb-4">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-600"><CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button variant="outline" className="w-full rounded-full" disabled>Current Plan</Button>
                  ) : isUpgrade ? (
                    <Button className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => handleUpgradePlan(plan.id)} disabled={changingPlan} data-testid={`upgrade-to-${plan.id}-btn`}>
                      {changingPlan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-1" />}
                      Upgrade to {plan.name}
                    </Button>
                  ) : isDowngrade ? (
                    <Button variant="outline" className="w-full rounded-full text-zinc-600" onClick={() => handleScheduleDowngrade(plan.id)} disabled={changingPlan} data-testid={`downgrade-to-${plan.id}-btn`}>
                      {changingPlan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Downgrade (effective next billing cycle)
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-1 pt-2">
            <button onClick={() => setBillingCadence("monthly")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${billingCadence === "monthly" ? "bg-zinc-900 text-white" : "text-zinc-400"}`}>Monthly</button>
            <button onClick={() => setBillingCadence("annual")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${billingCadence === "annual" ? "bg-zinc-900 text-white" : "text-zinc-400"}`}>Annual <span className="text-indigo-400">-20%</span></button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
